import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to sign in");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-10 bg-zinc-950 text-white">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-white text-zinc-950 flex items-center justify-center font-semibold">L</div>
          <span className="font-display text-lg font-semibold tracking-tight">Ledgerly</span>
        </div>
        <div>
          <div className="label-kicker text-zinc-400">Expense operations</div>
          <h1 className="font-display text-5xl font-semibold tracking-tight mt-3 max-w-md leading-[1.05]">
            Submit, review, and reconcile expenses in minutes — not days.
          </h1>
          <p className="mt-6 text-zinc-400 max-w-sm leading-relaxed">
            Snap a receipt and our AI fills the form for you. Managers approve with a single click.
            Workspaces are auto-created per company domain.
          </p>
        </div>
        <div className="text-xs text-zinc-500">© {new Date().getFullYear()} Ledgerly · Built for finance teams</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-up">
          <div className="mb-8">
            <div className="label-kicker">Welcome back</div>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-zinc-950 mt-1">Sign in to Ledgerly</h2>
            <p className="mt-2 text-sm text-zinc-500">Enter your work credentials to continue.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-zinc-600">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950 bg-white"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950 bg-white"
                placeholder="••••••••"
              />
            </div>

            {error ? (
              <div data-testid="login-error" className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
                {error}
              </div>
            ) : null}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-zinc-950 text-white px-4 py-2.5 text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"} <ArrowRight className="w-4 h-4" />
            </button>

            <div className="text-sm text-zinc-500 text-center">
              Don't have an account?{" "}
              <Link to="/register" data-testid="go-register-link" className="text-zinc-900 font-medium hover:underline">
                Create one
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
